import React, {useEffect, useMemo, useState} from 'react'
import {
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Stack,
} from '@mui/material'
import {
  RemoveCircleOutline,
  DragIndicator,
  AddReactionOutlined,
} from '@mui/icons-material'
import EmojiPicker, {Emoji} from 'emoji-picker-react'
import WbTextField from '../../../../common/WbTextField'
import WbSwitch from '../../../../common/WbSwitch'
import WbButton from '../../../../common/WbButton'
import WbOutlineButton from '../../../../common/WbOutlineButton'
import WbModal from '../../../../common/WbModal'
import WbAlert from '../../../../common/WbAlert'
import {LIKERT_OPTION} from '../../../../../utils/constants'
import customLikertScalesApi from '../../../../../services/api/questionLibrary/customLikertScalesApi'
import {getApiHookParams} from '../../../../../auth'
import {
  initialLikertSource,
  seedOptionsFromQuestion,
  toMultiOptions,
  nextOptionValue,
  scoreForOption,
  moveOption,
  optionEmoji,
  emojiPatch,
} from './likertScaleSource'

// Resolve the current tenant (trust vs school) exactly like CategoryPicker so saved
// scales are isolated per organisation.
function getTenantContext(survey) {
  const apiParams = getApiHookParams()
  const isMat = apiParams.groupId > 0 && apiParams.schoolId === null
  return {
    matId: isMat ? apiParams.groupId : 0,
    schoolId: isMat ? 0 : (apiParams.schoolId ?? survey?.schoolId ?? 0),
  }
}


// Saved scales have no name in the design — they're labelled by their joined option text.
const joinedLabel = options =>
  options
    .map(o => (o.text ?? '').toString().trim())
    .filter(Boolean)
    .join(', ')

export default function LikertScaleSelector({
  survey,
  question,
  updateQuestion,
  isPriority,
  showNumValues,
  audience,
}) {
  const context = useMemo(() => getTenantContext(survey), [survey])

  const [savedScales, setSavedScales] = useState([])
  // source: 'builder' | 'predefined' | 'custom' (mutually exclusive)
  const [source, setSource] = useState(() => initialLikertSource(question))
  const [editingOptions, setEditingOptions] = useState([])
  const [saveState, setSaveState] = useState('idle') // idle | saving | saved
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [emojiPickerRow, setEmojiPickerRow] = useState(null)
  // Dropdown selections are held locally because the backend Question doesn't persist
  // customLikertScaleId — a question save refetches and would otherwise blank the dropdown.
  const [selectedCustomId, setSelectedCustomId] = useState(
    question?.customLikertScaleId ?? ''
  )
  const [selectedPredefined, setSelectedPredefined] = useState(
    question?.likertOption || LIKERT_OPTION[0].value
  )

  const loadScales = () =>
    customLikertScalesApi
      .getForBuilder({matId: context.matId, schoolId: context.schoolId})
      .then(res => {
        const list = Array.isArray(res) ? res : []
        setSavedScales(list)
        return list
      })
      .catch(() => {
        setSavedScales([])
        return []
      })

  useEffect(() => {
    loadScales()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.matId, context.schoolId])

  // Seed the builder rows from the question's current options on mount.
  useEffect(() => {
    setEditingOptions(seedOptionsFromQuestion(question, audience))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const applyPredefined = opt => {
    updateQuestion({
      ...question,
      likertOption: opt.value,
      customLikertScaleId: null,
      multiOptionResponseAttributes: toMultiOptions(
        opt.label.split(',').map(t => ({text: t.trim()}))
      ),
      followupQuestions: [...question.followupQuestions],
      questionSettings: {...question.questionSettings},
    })
  }

  const applySavedScale = scale => {
    const sorted = [...(scale.options ?? [])].sort((a, b) => a.order - b.order)
    updateQuestion({
      ...question,
      likertOption: null,
      customLikertScaleId: scale.id,
      multiOptionResponseAttributes: toMultiOptions(
        sorted.map(o => ({text: o.text, value: o.value}))
      ),
      followupQuestions: [...question.followupQuestions],
      questionSettings: {...question.questionSettings},
    })
  }

  const applyEditing = options => {
    updateQuestion({
      ...question,
      likertOption: null,
      customLikertScaleId: null,
      multiOptionResponseAttributes: toMultiOptions(options),
      followupQuestions: [...question.followupQuestions],
      questionSettings: {...question.questionSettings},
    })
  }

  // ----- mutually exclusive source toggles -----
  const selectPredefined = checked => {
    if (!checked) {
      setSource('builder')
      applyEditing(editingOptions)
      return
    }
    setSource('predefined')
    const current =
      LIKERT_OPTION.find(x => x.value === selectedPredefined) ?? LIKERT_OPTION[0]
    setSelectedPredefined(current.value)
    applyPredefined(current)
  }

  const selectCustom = checked => {
    if (!checked) {
      setSource('builder')
      applyEditing(editingOptions)
      return
    }
    setSource('custom')
    const current = savedScales.find(x => x.id === selectedCustomId) ?? savedScales[0]
    if (current) {
      setSelectedCustomId(current.id)
      applySavedScale(current)
    }
  }

  // ----- builder editing -----
  const updateEditingOption = (index, patch) => {
    const next = editingOptions.map((o, i) => (i === index ? {...o, ...patch} : o))
    setEditingOptions(next)
    setSaveState('idle')
    applyEditing(next)
  }
  const addEditingOption = () => {
    const next = [...editingOptions, {text: '', value: nextOptionValue(editingOptions)}]
    setEditingOptions(next)
    setSaveState('idle')
    applyEditing(next)
  }
  const removeEditingOption = index => {
    const next = editingOptions.filter((_, i) => i !== index)
    setEditingOptions(next)
    setSaveState('idle')
    applyEditing(next)
  }
  const moveEditingOption = (from, to) => {
    const next = moveOption(editingOptions, from, to)
    setEditingOptions(next)
    setSaveState('idle')
    applyEditing(next)
  }
  // Set (unified codepoint) or clear (null) an option's emoji for the audience being edited.
  const setOptionEmoji = (index, unified) => {
    updateEditingOption(index, emojiPatch(audience, isPriority, unified))
  }

  const validOptions = editingOptions.filter(o => (o.text ?? '').trim().length > 0)
  const canSave = validOptions.length >= 2

  const saveForFutureUse = () => {
    if (!canSave) return
    setSaveState('saving')
    customLikertScalesApi
      .create({
        name: joinedLabel(editingOptions),
        matId: context.matId,
        schoolId: context.schoolId,
        options: validOptions.map((o, i) => ({
          text: o.text.trim(),
          value: scoreForOption(o.value, i),
          order: i + 1,
        })),
      })
      .then(async res => {
        setSaveState('saved')
        setShowSaveModal(false)
        await loadScales()
        WbAlert({
          message:
            res?.created === false
              ? 'This scale is already saved for your organisation'
              : 'Custom scale saved',
          type: 'success',
        })
      })
      .catch(() => {
        setSaveState('idle')
        setShowSaveModal(false)
        WbAlert({message: 'Failed to save custom scale', type: 'error'})
      })
  }

  return (
    <Grid container>
      {/* Source toggles (mutually exclusive). 'Custom' only appears once a scale exists. */}
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <FormControlLabel
          control={
            <WbSwitch
              size="small"
              sx={{marginLeft: '5px'}}
              checked={source === 'predefined'}
              disabled={!isPriority}
              onChange={e => selectPredefined(e.target.checked)}
            />
          }
          label="Predefined likert answers"
        />
      </Grid>
      {savedScales.length > 0 && (
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <FormControlLabel
            control={
              <WbSwitch
                size="small"
                sx={{marginLeft: '5px'}}
                checked={source === 'custom'}
                disabled={!isPriority}
                onChange={e => selectCustom(e.target.checked)}
              />
            }
            label="Custom likert answers"
          />
        </Grid>
      )}

      {/* Predefined dropdown */}
      {source === 'predefined' && (
        <Grid item lg={12} md={12} sm={12} xs={12} mt={2}>
          <WbTextField
            label="Predefined options"
            select
            disabled={!isPriority}
            value={selectedPredefined}
            onChange={e => {
              const opt = LIKERT_OPTION.find(x => x.value === Number(e.target.value))
              if (opt) {
                setSelectedPredefined(opt.value)
                applyPredefined(opt)
              }
            }}
          >
            {LIKERT_OPTION.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label
                  .split(',')
                  .map((t, i) => (showNumValues ? `${t.trim()} (${i + 1})` : t.trim()))
                  .join(', ')}
              </MenuItem>
            ))}
          </WbTextField>
        </Grid>
      )}

      {/* Custom (saved) dropdown — labelled by joined option text */}
      {source === 'custom' && (
        <Grid item lg={12} md={12} sm={12} xs={12} mt={2}>
          <WbTextField
            label="Custom options"
            select
            disabled={!isPriority}
            value={selectedCustomId}
            onChange={e => {
              const scale = savedScales.find(x => x.id === Number(e.target.value))
              if (scale) {
                setSelectedCustomId(scale.id)
                applySavedScale(scale)
              }
            }}
          >
            {savedScales.map(scale => (
              <MenuItem key={scale.id} value={scale.id}>
                {scale.name ||
                  joinedLabel((scale.options ?? []).map(o => ({text: o.text})))}
              </MenuItem>
            ))}
          </WbTextField>
        </Grid>
      )}

      {/* Builder: editable answer rows + Save for future use */}
      {source === 'builder' && (
        <Grid item lg={12} md={12} sm={12} xs={12} mt={2}>
          <Grid container spacing={1} alignItems="center">
            {editingOptions.map((opt, i) => {
              const emoji = optionEmoji(opt, audience)
              return (
                <Grid
                  item
                  xs={12}
                  key={i}
                  draggable={isPriority}
                  onDragStart={e => {
                    if (!isPriority) return
                    e.dataTransfer.setData('text/plain', String(i))
                  }}
                  onDragOver={e => {
                    if (isPriority) e.preventDefault()
                  }}
                  onDrop={e => {
                    if (!isPriority) return
                    e.preventDefault()
                    const from = parseInt(e.dataTransfer.getData('text/plain'), 10)
                    if (!Number.isNaN(from)) moveEditingOption(from, i)
                  }}
                >
                  <Grid container spacing={1} alignItems="center" wrap="nowrap">
                    <Grid item>
                      <IconButton
                        disabled={!isPriority}
                        style={{cursor: 'grab'}}
                        size="small"
                      >
                        <DragIndicator sx={{fontSize: '20px'}} />
                      </IconButton>
                    </Grid>
                    <Grid item style={{lineHeight: 0}}>
                      {isPriority ? (
                        <IconButton
                          size="small"
                          style={{padding: 4}}
                          title={emoji ? 'Change emoji' : 'Add emoji'}
                          onClick={() =>
                            setEmojiPickerRow(emojiPickerRow === i ? null : i)
                          }
                        >
                          {emoji ? (
                            <Emoji unified={emoji} size={22} />
                          ) : (
                            <AddReactionOutlined
                              sx={{fontSize: '20px', color: '#8a8a8a'}}
                            />
                          )}
                        </IconButton>
                      ) : (
                        emoji && <Emoji unified={emoji} size={22} />
                      )}
                    </Grid>
                    <Grid item xs>
                      <WbTextField
                        label={`Option ${i + 1}`}
                        value={opt.text}
                        disabled={!isPriority}
                        onChange={e => updateEditingOption(i, {text: e.target.value})}
                        fullWidth
                      />
                    </Grid>
                    {showNumValues && (
                      <Grid item xs={3} sm={2}>
                        <WbTextField
                          label="Value"
                          type="number"
                          value={opt.value}
                          disabled={!isPriority}
                          onChange={e => updateEditingOption(i, {value: e.target.value})}
                          fullWidth
                        />
                      </Grid>
                    )}
                    <Grid item>
                      {editingOptions.length > 2 && (
                        <IconButton
                          disabled={!isPriority}
                          onClick={() => removeEditingOption(i)}
                        >
                          <RemoveCircleOutline className="icon-delete" />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              )
            })}

            {emojiPickerRow != null && isPriority && (
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <div style={{position: 'relative', zIndex: 10}}>
                  <EmojiPicker
                    skinTonesDisabled
                    onEmojiClick={e => {
                      setOptionEmoji(emojiPickerRow, e.unified)
                      setEmojiPickerRow(null)
                    }}
                  />
                  {optionEmoji(editingOptions[emojiPickerRow], audience) && (
                    <Link
                      href="#"
                      underline="hover"
                      onClick={e => {
                        e.preventDefault()
                        setOptionEmoji(emojiPickerRow, null)
                        setEmojiPickerRow(null)
                      }}
                    >
                      Remove emoji
                    </Link>
                  )}
                </div>
              </Grid>
            )}

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Link
                  href="#"
                  underline="hover"
                  onClick={e => {
                    e.preventDefault()
                    if (isPriority && editingOptions.length < 10) addEditingOption()
                  }}
                  style={{
                    pointerEvents:
                      isPriority && editingOptions.length < 10 ? 'auto' : 'none',
                    opacity: isPriority && editingOptions.length < 10 ? 1 : 0.5,
                  }}
                >
                  + Add an answer choice
                </Link>

                {saveState === 'saved' ? (
                  <FormControlLabel
                    control={<Checkbox checked disabled size="small" />}
                    label="Saved"
                  />
                ) : (
                  <WbButton
                    CustomButtonText="Save for future use"
                    onClick={() => setShowSaveModal(true)}
                    disabled={!canSave || !isPriority}
                  />
                )}
              </Stack>
            </Grid>
          </Grid>
        </Grid>
      )}

      {showSaveModal && (
        <WbModal
          title="Save Likert Options"
          onClose={() => setShowSaveModal(false)}
          width="35%"
          content={
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12}>
                Do you want to save likert option for future use?
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <WbOutlineButton
                    CustomOutlineButtonText="Cancel"
                    onClick={() => setShowSaveModal(false)}
                    disabled={saveState === 'saving'}
                  />
                  <WbButton
                    CustomButtonText="Save"
                    onClick={saveForFutureUse}
                    disabled={saveState === 'saving' || !canSave}
                  />
                </Stack>
              </Grid>
            </Grid>
          }
        />
      )}
    </Grid>
  )
}
